import { useState, useEffect, useRef } from "react";
import useAuthStore from "../../store/authStore";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import AdminLayout from "../../layouts/AdminLayout";
import ManagerLayout from "../../layouts/ManagerLayout";
import EmployeeProgress from "../../pages/employee/EmployeeProjectProgress";
import EmployeeDailyProgress from "../employee/EmployeeActivityProgress";
import ManagerProgress from "../../pages/manager/ManagerProgress";
import { getUserById, deleteUser } from "../../api/accountsService";
import apiClient from "../../api/apiClient";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function AdminUserDetails() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const role = useAuthStore((state) => state.role);
  const loggedInId = useAuthStore((state) => state.employeeId);
  const isReadOnly = location.state?.readOnly === true;

  const Layout =
    role === "ADMIN" ? AdminLayout : role === "MANAGER" ? ManagerLayout : AdminLayout;

  const [user, setUser] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState("/images/avatar.png");
  const [loading, setLoading] = useState(true);
  const [activePanel, setActivePanel] = useState(null);
  const [viewerIsSuperAdmin, setViewerIsSuperAdmin] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (role !== "ADMIN" || !loggedInId) return;
    apiClient
      .get(`/accounts/api/users/${loggedInId}/`)
      .then((res) => setViewerIsSuperAdmin(res.data.user?.is_superuser === true))
      .catch(() => setViewerIsSuperAdmin(false));
  }, [role, loggedInId]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const userRes = await getUserById(employeeId);
        const userData = userRes.data.user;
        setUser(userData);
        if (userData?.photo) {
          const photo = userData.photo;
          const photoPath = photo.startsWith("http") ? photo : photo.startsWith("/") ? photo : `/media/${photo}`;
          setProfilePhoto(`${photoPath}?t=${Date.now()}`);
        } else {
          setProfilePhoto("/images/avatar.png");
        }
      } catch (err) {
        console.error("Failed loading details:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [employeeId]);

  const targetIsAdmin = user?.role === "ADMIN";
  const canEditDelete = role === "ADMIN" && !isReadOnly && (viewerIsSuperAdmin || !targetIsAdmin);

  const handleDeleteClick = () => { setDeleteError(""); setCaptchaToken(null); setShowDeleteModal(true); recaptchaRef.current?.reset(); };
  const handleDeleteCancel = () => { setShowDeleteModal(false); setDeleteError(""); setCaptchaToken(null); recaptchaRef.current?.reset(); };

  const handleDelete = async () => {
    if (!captchaToken) { setDeleteError("Please complete the CAPTCHA verification to continue."); return; }
    setDeleteLoading(true); setDeleteError("");
    try {
      await deleteUser(employeeId, captchaToken);
      alert("Employee deleted successfully");
      navigate("/admin/users");
    } catch (err) {
      const backendError = err?.response?.data?.detail || err?.response?.data?.error || "Failed to delete employee. Please try again.";
      setDeleteError(backendError); setCaptchaToken(null); recaptchaRef.current?.reset();
    } finally { setDeleteLoading(false); }
  };

  if (loading) return <Layout><div className="p-6 sm:p-10 text-white text-center">Loading employee data...</div></Layout>;
  if (!user) return <Layout><div className="p-6 sm:p-10 text-red-400 text-center">User not found</div></Layout>;

  const handleBack = () => { if (location.state?.from) navigate(location.state.from); else navigate("/admin/users"); };
  const togglePanel = (panel) => setActivePanel((prev) => (prev === panel ? null : panel));

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6 py-4 sm:py-8 px-3 sm:px-0">

        {/* Profile Section */}
        {role !== "EMPLOYEE" && (
          <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-4 sm:p-6 max-w-4xl mx-auto">
            {canEditDelete && (
              <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-0 sm:absolute sm:top-8 sm:right-8">
                <button onClick={() => navigate(`/admin/update-user/${employeeId}`, { state: { from: location.pathname } })}
                  className="flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs sm:text-sm font-semibold shadow-lg hover:scale-105 transition-all duration-300">Edit</button>
                <button onClick={handleDeleteClick}
                  className="flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white text-xs sm:text-sm font-semibold shadow-lg hover:scale-105 transition-all duration-300">Delete</button>
              </div>
            )}
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">Employee Profile</h2>
            <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
              <img src={profilePhoto} alt="profile"
                className="w-16 h-16 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-white/20 shadow-xl shrink-0"
                onError={(e) => { e.target.src = "/images/avatar.png"; }} />
              <div className="min-w-0">
                <h3 className="text-lg sm:text-2xl font-bold text-white truncate">{user.full_name}</h3>
                <p className="text-white/60 text-sm">{user.designation || "—"}</p>
                <p className="text-white/40 text-xs">{user.department || "—"}</p>
                {user.is_superuser && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-300 text-[10px] font-bold">👑 Super Admin</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <ProfileField label="Employee ID" value={user.employee_id} icon="🆔" />
              <ProfileField label="Email" value={user.email} icon="📧" />
              <ProfileField label="Role" value={user.is_superuser ? "Super Admin" : user.role === "ADMIN" ? "Admin" : user.role} icon="👔" />
              <ProfileField label="Department" value={user.department || "—"} icon="🏢" />
              <ProfileField label="Designation" value={user.designation || "—"} icon="💼" />
              <ProfileField label="Manager" value={user.manager_display || "—"} icon="🧑‍💼" />
              <ProfileField label="Mobile Number" value={user.mobile_number || "—"} icon="📱" />
              <ProfileField label="Location" value={user.location_name || user.location || "—"} icon="📍" />
              <ProfileField label="Date of Birth" value={user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString("en-GB") : "—"} icon="🎂" />
              <ProfileField label="Gender" value={user.gender === "FEMALE" ? "Female" : "Male"} icon="🚻" />
              <ProfileField label="Date of Joining" value={user.date_joined ? new Date(user.date_joined).toLocaleDateString("en-GB") : "—"} icon="📅" />
            </div>
          </div>
        )}

        {/* Progress Section */}
        <div className="max-w-6xl mx-auto px-0">

          {/* Toggle Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 px-3 sm:px-0 mb-4">
            <button
              onClick={() => togglePanel("project")}
              className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all shadow-lg border
                ${activePanel === "project"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 border-blue-400/40 text-white scale-[1.01]"
                  : "bg-white/10 hover:bg-white/15 border-white/20 text-white/80 hover:text-white"}`}
            >
              <span>📊</span>
              <span>View Project Progress</span>
              <span className="ml-auto text-xs opacity-60">{activePanel === "project" ? "▲ Hide" : "▼ Show"}</span>
            </button>

            {!(user?.role === "MANAGER" || user?.role === "ADMIN") && (
              <button
                onClick={() => togglePanel("activity")}
                className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all shadow-lg border
                  ${activePanel === "activity"
                    ? "bg-gradient-to-r from-emerald-500 to-teal-500 border-emerald-400/40 text-white scale-[1.01]"
                    : "bg-white/10 hover:bg-white/15 border-white/20 text-white/80 hover:text-white"}`}
              >
                <span>🗂️</span>
                <span>View Activity Progress</span>
                <span className="ml-auto text-xs opacity-60">{activePanel === "activity" ? "▲ Hide" : "▼ Show"}</span>
              </button>
            )}
          </div>

          {/* Project Progress Panel */}
          {activePanel === "project" && (
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-4 sm:p-6 mb-4">
              {user?.role === "MANAGER" || user?.role === "ADMIN" ? (
                <ManagerProgress managerId={employeeId} embedMode={true} />
              ) : (
                <EmployeeProgress employeeId={employeeId} embedMode={true} />
              )}
            </div>
          )}

          {/* Activity Progress Panel — embedMode=true skips EmployeeLayout + back button */}
          {activePanel === "activity" && (
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-4 sm:p-6 mb-4">
              <EmployeeDailyProgress employeeId={employeeId} embedMode={true} />
            </div>
          )}

          {/* Back Button */}
          <div className="mt-4 flex justify-start px-3 sm:px-0">
            <button onClick={handleBack}
              className="px-4 sm:px-6 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold text-sm sm:text-base transition-colors">
              ← Back to Users
            </button>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-white/20 rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-red-400 text-xl">⚠️</span>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Delete Employee</h3>
                <p className="text-white/50 text-xs">This action cannot be undone</p>
              </div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-5">
              <p className="text-red-300 text-sm">
                You are about to permanently delete{" "}
                <span className="font-bold text-white">{user?.full_name}</span> ({employeeId}). All associated data will be removed.
              </p>
            </div>
            <div className="mb-4">
              <p className="text-white/60 text-xs mb-2 font-medium">Complete the verification below to confirm deletion:</p>
              <div className="flex justify-center overflow-x-auto">
                <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => { setCaptchaToken(token); setDeleteError(""); }}
                  onExpired={() => { setCaptchaToken(null); setDeleteError("CAPTCHA expired. Please complete it again."); }}
                  onErrored={() => { setCaptchaToken(null); setDeleteError("CAPTCHA failed to load. Please refresh the page."); }}
                  theme="dark" />
              </div>
            </div>
            {deleteError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-2.5 mb-4">
                <p className="text-red-300 text-xs font-medium">⚠️ {deleteError}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={handleDeleteCancel} disabled={deleteLoading}
                className="flex-1 py-2.5 rounded-xl border border-white/20 text-white/70 hover:bg-white/10 font-semibold text-sm transition disabled:opacity-50">Cancel</button>
              <button onClick={handleDelete} disabled={!captchaToken || deleteLoading}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-bold text-sm shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed hover:from-red-600 hover:to-red-700">
                {deleteLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting…
                  </span>
                ) : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function ProfileField({ label, value, icon }) {
  return (
    <div className="flex gap-3 px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 rounded-xl border border-white/10">
      <div className="text-base sm:text-lg shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-white/50">{label}</div>
        <div className="text-xs sm:text-sm font-bold text-white break-words">{value}</div>
      </div>
    </div>
  );
}

export default AdminUserDetails;
